export interface CallCapabilities {
  secureContext: boolean;
  webCrypto: boolean;
  webRtc: boolean;
  workers: boolean;
  encryptedMedia: boolean;
  captureDevices: boolean;
  displayCapture: boolean;
}

export const ENCRYPTED_CALL_UNAVAILABLE = "This browser or desktop webview cannot run Link's end-to-end encrypted calls. Update your app/system or use a supported browser. Link will not start an unencrypted call.";
export const SCREEN_CAPTURE_UNAVAILABLE = "Screen sharing is not available in this browser or desktop webview. Your call can stay encrypted, but no screen will be captured. Use a supported browser until native sharing is available on this platform.";
export const ENCRYPTION_LOST = "Call encryption could not be maintained. Link stopped the call instead of sending unencrypted media.";

export function requireEncryptedCallSupport(capabilities: CallCapabilities): void {
  if (!capabilities.secureContext || !capabilities.webCrypto || !capabilities.webRtc ||
      !capabilities.workers || !capabilities.encryptedMedia || !capabilities.captureDevices) {
    throw new Error(ENCRYPTED_CALL_UNAVAILABLE);
  }
}

export function requireScreenCaptureSupport(capabilities: CallCapabilities): void {
  requireEncryptedCallSupport(capabilities);
  if (!capabilities.displayCapture) throw new Error(SCREEN_CAPTURE_UNAVAILABLE);
}

/** The same room encryption requirement applies to microphone, camera and screen. */
export function requireRoomEncryption(room: { isE2EEEnabled: boolean; localParticipant: { isE2EEEnabled: boolean } }): void {
  if (!room.isE2EEEnabled || !room.localParticipant.isE2EEEnabled) throw new Error(ENCRYPTION_LOST);
}

/** LiveKit acknowledges worker encryption asynchronously, after connecting.
 * Never publish media while that acknowledgement is still pending. */
export async function waitForEncryptionReady(options: {
  ready: () => boolean;
  subscribe: (check: () => void, fail: () => void) => () => void;
  timeoutMs?: number;
}): Promise<void> {
  if (options.ready()) return;
  await new Promise<void>((resolve, reject) => {
    let unsubscribe = () => {};
    let finished = false;
    const timer = setTimeout(() => finish(false), options.timeoutMs ?? 8000);
    const finish = (success: boolean) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      unsubscribe();
      if (success) resolve(); else reject(new Error(ENCRYPTION_LOST));
    };
    const check = () => {
      try { if (options.ready()) finish(true); }
      catch { finish(false); }
    };
    try {
      unsubscribe = options.subscribe(check, () => finish(false));
      if (finished) unsubscribe(); else check();
    } catch { finish(false); }
  });
}
